import { useMutation } from '@apollo/client';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import {
  InsertRibbonItemsByParentTypeDocument,
  namedOperations,
  UpdateRibbonItemsByParentTypeDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useTranslation } from 'react-i18next';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpsertCustomRibbonTRPCInput } from './useUpsertCustomRibbonTRPC';
import { useUpsertCustomRibbonTRPC } from './useUpsertCustomRibbonTRPC';

export const useUpsertCustomRibbon = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const { addNotification } = useNotifications();
  const { t } = useTranslation(['common'], {
    keyPrefix: 'customisableRibbons',
  });

  const [insertGraphQL, insertState] = useMutation(
    InsertRibbonItemsByParentTypeDocument,
    {
      update: (cache) => {
        evictField(cache, 'custom_ribbon');
      },
      refetchQueries: [namedOperations.Query.getRibbonItemsByParentType],
    }
  );

  const [updateGraphQL, updateState] = useMutation(
    UpdateRibbonItemsByParentTypeDocument,
    {
      update: (cache) => {
        evictField(cache, 'custom_ribbon');
      },
      refetchQueries: [namedOperations.Query.getRibbonItemsByParentType],
    }
  );

  const trpcMutation = useUpsertCustomRibbonTRPC();

  const upsertCustomRibbon = async (input: UpsertCustomRibbonTRPCInput) => {
    if (trpcEnabled) {
      return trpcMutation.upsertCustomRibbon(input);
    }

    // GraphQL fallback: use update mutation when OriginalTimestamp is provided
    // (preserves optimistic locking), otherwise use insert (upsert via ON CONFLICT)
    if (input.OriginalTimestamp) {
      const result = await updateGraphQL({
        variables: {
          id: input.Id,
          parentType: input.ParentType,
          filters: input.Filters,
          originalTimestamp: input.OriginalTimestamp,
        },
      });

      if (result.data?.update_custom_ribbon?.affected_rows !== 1) {
        addNotification({
          type: 'error',
          content: t('record_updated_by_another_user'),
        });

        throw new Error(
          'Records not updated. Record may have been updated by another user'
        );
      }
    } else {
      await insertGraphQL({
        variables: {
          parentType: input.ParentType,
          filters: input.Filters,
        },
      });
    }
  };

  if (trpcEnabled) {
    return {
      upsertCustomRibbon,
      loading: trpcMutation.loading,
    };
  }

  return {
    upsertCustomRibbon,
    loading: insertState.loading || updateState.loading,
  };
};
